import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { aclAPI, PermissionCheckRequest, PermissionCheckResponse, FieldPermissionsResponse, bulkPermissionResponseKey } from '../api/aclApi';

interface ACLContextType {
    checkPermission: (request: PermissionCheckRequest) => Promise<PermissionCheckResponse>;
    checkBulkPermissions: (requests: PermissionCheckRequest[]) => Promise<Record<string, boolean>>;
    getFieldPermissions: (collection: string, action: 'field_read' | 'field_update') => Promise<FieldPermissionsResponse>;
    canPerformAction: (action: string, targetType: 'item' | 'collection', targetId: string) => Promise<boolean>;
}

const ACLContext = createContext<ACLContextType | undefined>(undefined);

interface ACLProviderProps {
    children: ReactNode;
}

export const ACLProvider: React.FC<ACLProviderProps> = ({ children }) => {

    /**
     * Check if user has permission to perform an action
     */
    const checkPermission = useCallback(async (request: PermissionCheckRequest): Promise<PermissionCheckResponse> => {
        try {
            return await aclAPI.checkPermission(request);
        } catch (error: any) {
            console.error('ACL Context: Error checking permission:', error);
            // Default to denied on error
            return {
                allowed: false,
                reason: error.message || 'Permission check failed'
            };
        }
    }, []);

    /**
     * Bulk check multiple permissions
     */
    const checkBulkPermissions = useCallback(async (requests: PermissionCheckRequest[]): Promise<Record<string, boolean>> => {
        try {
            return await aclAPI.checkBulkPermissions(requests);
        } catch (error: any) {
            console.error('ACL Context: Error checking bulk permissions:', error);
            // Default to all denied on error
            const results: Record<string, boolean> = {};
            requests.forEach((req) => {
                results[bulkPermissionResponseKey(req)] = false;
            });
            return results;
        }
    }, []);

    /**
     * Get field-level permissions for a collection
     */
    const getFieldPermissions = useCallback(async (
        collection: string,
        action: 'field_read' | 'field_update'
    ): Promise<FieldPermissionsResponse> => {
        try {
            return await aclAPI.getFieldPermissions(collection, action);
        } catch (error: any) {
            console.error('ACL Context: Error getting field permissions:', error);
            // Default to no fields allowed on error
            return {
                allowedFields: [],
                deniedFields: []
            };
        }
    }, []);

    /**
     * Simplified helper: Check if user can perform an action
     * Returns boolean directly instead of full response
     */
    const canPerformAction = useCallback(async (
        action: string,
        targetType: 'item' | 'collection',
        targetId: string
    ): Promise<boolean> => {
        try {
            const result = await checkPermission({
                action: action as any,
                targetType,
                targetId
            });
            return result.allowed;
        } catch (error) {
            console.error('ACL Context: Error in canPerformAction:', error);
            return false;
        }
    }, [checkPermission]);

    const value: ACLContextType = {
        checkPermission,
        checkBulkPermissions,
        getFieldPermissions,
        canPerformAction
    };

    return (
        <ACLContext.Provider value={value}>
            {children}
        </ACLContext.Provider>
    );
};

/**
 * Hook to use ACL context
 */
export const useACL = (): ACLContextType => {
    const context = useContext(ACLContext);
    if (context === undefined) {
        throw new Error('useACL must be used within an ACLProvider');
    }
    return context;
};

export default ACLContext;
